/* event handlers, state, and state management for mouse events */

import { LOG, display_message_to_user } from '@phi/logging/frontend';
import {
    animate,
    stop_adding_vertex,
    switch_mirror_plane_perpendicular_view,
    switch_symmetry_plane_perpendicular_view,
    hide_halo,
    highlight_objects_while_hovering,
    show_crosshair,
    hide_edge_midpoint,
    show_edge_midpoint,
    hide_crosshair,
    attempt_to_attach_working_plane_to_face_for_dragging,
    interrupt_offset_face,
    add_vertex_on_one_or_more_selected_edges_with_shift_pressed,
    stop_drawing_edge_on_face,
    switch_view,
    complete_setting_symmetry_plane,
    complete_setting_mirror_plane,
    zoom,
} from '../scene';
import {
    set_default_face_color,
    set_default_vertex_color,
    uid_unhighlight_all_objects,
    uid_unhighlight_object,
} from '../colours_materials_appearance';
import * as THREE from 'three';
import { hide_modal } from '../modals';
import App from '../application-container';
import {
    line_point_distance,
    project_point_on_line,
} from '../vector3';
import {
    uil_add_new_vertex,
    uil_drag_vertex,
    uil_end_drag_edge,
    uil_end_drag_manipulator,
    uil_end_drag_vertex,
    uil_get_body_of_edge,
    uil_get_body_of_face,
    uil_get_body_of_vertex,
    uil_get_edge_tangent_at_vertex,
    uil_get_vertices_of_edge,
    uil_is_edge_linear,
    uil_is_object_visible,
    uil_is_vertex_of_face,
} from '../ui_logic';
import {
    complete_making_edges_perpendicular_to_plane,
} from '../uid_continuity';
import { display_read_value_modal } from '../state/modals';
import { ctrl_pressed, handle_right_click, shift_pressed } from '../keypress';
import {
    find_camera_target_point_for_rotation,
    show_rotation_center,
    hide_rotation_center,
    mouse_near_edges,
    // check_model_integrity,
    suppress_animate,
    rotate_viewpoint_actions,
    unsuppress_animate_if_ready,
    raycaster_intersect_objects,
    find_intersection_object,
    find_all_intersection_objects_relevant_to_face,
    add_small_ball,
    adjust_camera_position_along_axis_and_depth,
    identify_snapping_targets,
    calculate_halo_difference,
    find_intersection_phi_or_reference_object,
    find_currently_pointed_object,
    must_view_down_negative_direction,
    request_setting_button_states,
    get_display_unit_from_unitless_value,
    object_is_selected,
    console_log_xml,
    unsuppress_undo_tracking_if_ready,
    set_linear_draw_direction,
    //set_horizontal_vertical_rotate_direction,
    reset_horizontal_vertical_rotate_direction,
} from '../utility';
import {
    abort_snap_to_object,
    display_read_coords_modal,
    finish_snap_to_object,
    uid_deselect_all_objects,
    uid_deselect_object,
    uid_hide_rigid_move_manipulator,
    uid_offset_face_drag_step,
    uid_select_object,
    uid_start_offset_face,
    uid_toggle_edge_tangent_manipulators,
    uid_toggle_select_object,
    uid_toggle_select_reference_face,
} from '../ui_display';
import {
    from_3d_coordinates_to_2d_screen_coordinates,
    get_grid_point,
    plane_ray_intersect,
    triangle_normal,
} from '../geometry_and_math';
import type { Edge, Face, Vertex } from '@phi/jcore';
import { get_core_side_dragging_state } from '../JCore/jcore';
import SelectionBox from '../SelectionBox';
import { set_button_states } from '../non_graphical_ui_actions';
import { select_edge_chain, vertices_of_face } from '../lattice';
import { Uid_vertex } from '../graph_objects';
import * as v2 from '@phi/maths/vector2';
import {
    intersection_to_uid_object,
    is_click,
    pointer_has_moved_a_little,
    pointer_normalised,
    pointer_normalised_prev,
    update_cursor_press_time_and_position,
    update_pointer_normalised,
} from '.';
import { sign } from '@vaaas/fpts/maths';
import {
    drag_clipping_plane,
    drag_face,
    find_intersection,
    handle_mousemove_while_clickdragging_edge,
    handle_mousemove_while_clickdragging_manipulator_element,
    mark_click_location_on_edge,
    pan,
    rotate,
} from './actions';
import { hide_floating_toolbar, showing_floating_toolbar } from '@features/floating-toolbar';
import { clipping_on } from '@features/clipping';
import { display_scale_modal } from '@phi/scale';

/** represents the different mouse buttons that can be pressed */
export enum MouseButton {
    /** no button */
    none = -1,

    /** usually left click */
    primary = 0,

    /** usually middle click */
    aux = 1,

    /** usually right click */
    secondary = 2,
}

let button_pressed = MouseButton.none;

export const is_button_pressed = () => button_pressed !== MouseButton.none;

export const is_this_button_pressed = (x: MouseButton) => button_pressed === x;

export function mousedown_handler(e: MouseEvent) {
    button_pressed = e.button;

    // Find normalized mouse coordinates and intersect scene from uid_camera
    update_pointer_normalised(e);

    // Prepare and raycaster and camera, and find intersections.
    const intersected_obj = find_intersection();

    // Reset the mouseDown time (we do it after raycasting which may take a long time)
    update_cursor_press_time_and_position(e);

    if (App.state.offset_face_selecting_vertex || App.state.offset_face_dragging)
        // If we're in the offset process we don't handle the mousedown event, we'll let the click event handler
        // do what it needs to do.
        return;

    else if (isMiddleClick(e))
        handle_press_down_for_panning();

    else if (isRightClick(e) && !App.state.panning && App.state.allow_rotate)
        handle_press_down_for_rotating();

    else if (intersected_obj == null && !App.state.panning && !App.state.rotating)
        handle_press_down_for_box_selecting();

    else if (intersected_obj)
        handle_press_down_on_intersected_element(intersected_obj, button_pressed ===  MouseButton.primary, shift_pressed());
}

export function mousemove_handler(e: PointerEvent) {
    update_pointer_normalised(e);

    // TODO: figure this thing out later...
    // if (is_button_pressed()) {
    //     // If the mouse is pressed and we exit the scene, force a mouseup handler
    //     if (mouse_near_edges(pointer_normalised, true) && !App.state.panning && !App.state.rotating)
    //         mouseup_handler(e);
    //     // Run consistency checks if required, unless the mouse button is not pressed, in which case nothing can have changed
    //     else if (should_run_consistency_checks()) {
    //         const issues = check_model_integrity(
    //             true,
    //             false,
    //             false,
    //             true,
    //             App.state.always_run_consistency_checks_on_vertices,
    //             App.state.always_run_consistency_checks_on_rest,
    //         );

    //         // Allow 7 drag steps before blocking
    //         if (
    //             issues > 0
    //             && App.state.interrupt_drag_at_failed_consistency_checks
    //             && uid_state.drag_step_count > 7
    //         )
    //             mouseup_handler(e);
    //     }
    // }

    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);

    if (App.state.snapping_to_object && is_button_pressed() && !App.state.rotating) {
        // Stop snapping if mouse moves pressed.
        abort_snap_to_object();
    }

    if (is_button_pressed()) {
        if (App.state.rotating) {
            //set_horizontal_vertical_rotate_direction(pointer_normalised, pointer_normalised_prev);
            rotate();
        } else if (App.state.panning) {
            pan();
        } else if (App.state.drawing_edge || App.state.carving)
            handle_mousemove_while_drawing_edge_on_face_event();
        else if (
            !uid_state.draggable_object_was_clicked
            && button_pressed === MouseButton.primary
            && !App.state.offset_face_dragging
            && !App.state.offset_face_selecting_vertex
            && uid_raynode == uid_objects_raynode
        )
            handle_mousemove_while_hovering_with_button_pressed_event();
        else if (button_pressed === MouseButton.secondary)
            rotate_viewpoint_actions();
        else if (clipping_on.value)
            drag_clipping_plane();
    }

    if (!App.state.panning && !App.state.rotating) {
        if (!is_button_pressed())
            // The user is simply hovering the mouse and the mouse button is not pressed. Just highlight stuff
            // Also check whether we are offsetting a face, in which case we may need to call an offset drag step
            handle_mousemove_with_mouse_button_not_pressed();

        else if (uid_state.manipulator_object_was_clicked)
            // The user is click-dragging a manipulator element.
            handle_mousemove_while_clickdragging_manipulator_element(shift_pressed());
        else if (uid_state.draggable_object_was_clicked) {
            // Check that we're not in a special state
            if (
                !App.state.adding_vertex
                && !uid_state.adding_edge_endpoints_status
                && !App.state.offset_face_selecting_vertex
            ) {
                // The user is click-dragging a draggable object.
                if (uid_state.in_first_drag_step && uid_drag_object.type != 'face')
                    uid_deselect_all_objects();

                if (!uid_state.in_first_drag_step || pointer_has_moved_a_little(e)) {
                    let res;
                    if (uid_drag_object.type == 'vertex')
                        res = handle_mousemove_while_clickdragging_vertex(pointer_normalised, shift_pressed());
                    else if (uid_drag_object.type == 'edge')
                        res = handle_mousemove_while_clickdragging_edge(pointer_normalised, shift_pressed());
                    else if (uid_drag_object.type == 'face')
                        res = drag_face(pointer_normalised);
                    else {
                        // In the future we can also handle the following cases:
                        // - The drag object is a vertex, but an adjacent edge was selected, so it's drag vertex on edge.
                        // - The drag object is a vertex or edge, but more than one objects were selected, so it's drag set.
                    }

                    if (res)
                        uid_state.in_first_drag_step = false;
                }
            }
        }
    }

    // Store previous mouse location
    v2.copy_into(pointer_normalised_prev)(pointer_normalised);
}

export function mouseup_handler(e: PointerEvent) {
    update_pointer_normalised(e);
    reset_horizontal_vertical_rotate_direction();

    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    if (App.state.snapping_to_object && !is_click(e) && !App.state.rotating) {
        // Stop snapping if it's not a click
        abort_snap_to_object();
    }

    // Set global flags to correct values
    button_pressed = MouseButton.none;
    uid_state.manipulator_object_was_clicked = false;
    uid_state.draggable_object_was_clicked = false;
    uid_state.right_mouse_button_is_pressed = false;
    uid_state.middle_mouse_button_is_pressed = false;
    App.state.panning = false;

    const finished_rotation = App.state.rotating;
    App.state.rotating = false;
    hide_rotation_center();

    // Prepare flag for next drag action
    uid_state.in_first_drag_step = true;

    // Distinguish from click:
    if ((is_click(e)) && (!uid_in_dragging_mode) && (get_core_side_dragging_state() == 'off' || get_core_side_dragging_state() == 'face_offset')) {
        // Here we handle real click events, i.e. not click-drag events.
        // Note that we also use the dragging state to distinguish between click and drag-end events.
        // In case we are dragging for face_offset, we allow handling clicks.
        if (isRightClick(e))
            handle_right_click(e);
        else
            handle_mouseup_click_events(shift_pressed());
        set_button_states();
    } else if (uid_in_dragging_mode || (uid_raynode == uid_manip_raynode && get_core_side_dragging_state() != 'off')) {
        handle_mouseup_drag_end_events(e);
        set_button_states();
    } else {
        handle_mouseup_nonclick_nondrag_events(finished_rotation);
    }
}

export function handle_mousemove_with_mouse_button_not_pressed() {
    // The user is simply hovering the mouse and the mouse button is not pressed. Just highlight stuff
    // Also check whether we are offsetting a face, in which case we may need to call an offset drag step

    highlight_objects_while_hovering(pointer_normalised);

    // Check whether we are offsetting a face, in which case we may need to call an offset drag step
    if (App.state.offset_face_dragging) {
        const p = plane_ray_intersect(offset_face_plane_base, offset_face_plane_normal, uid_raycaster.ray.origin, uid_raycaster.ray.direction, 10);
        // Find closest vertex to mouse
        let closest_vertex_id: Vertex | 0 = 0;
        let min_dist = 10000000000;
        offset_face_vertices.forEach(function (vid) {
            const v = uid_vertices.getItem(vid);
            let vp = null;
            if (v) {
                vp = v.object3d!.position;
                const dist = line_point_distance(uid_raycaster.ray.origin, uid_raycaster.ray.direction, vp);
                if (dist < min_dist) {
                    closest_vertex_id = vid;
                    min_dist = dist;
                }
            }
            if (closest_vertex_id == 0) {
                console_log_xml('ERROR getting closest vertex id while offsetting');
            }
        });
        // XXX uid_offset_face_drag_step( offset_face_object.id, closest_vertex_id, p );
        uid_offset_face_drag_step(offset_face_object.id, offset_face_vertex.id, p);
    } else if (uid_raynode == uid_objects_raynode && shift_pressed()) { // Check whether the shift button is pressed, in which case we display a different halo
        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
        if (intersects.length > 0) {
            const intersected_obj = find_intersection_object(intersects);
            if (intersected_obj) {
                const obj = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);
                if (obj && obj.type == 'face') {
                    hide_halo();

                    // If grid is visible repeat the intersection attempt with the snapped
                    if (App.state.grid_visible && snap_to_grid && viewing_down_orthogonal_plane) {
                        const normalized_on_grid = get_grid_point(pointer_normalised, uid_grid_origin, uid_grid_normal, uid_grid_min, uid_grid_max, uid_grid_step);
                        const mousenormalized_snapped = from_3d_coordinates_to_2d_screen_coordinates(normalized_on_grid);
                        uid_raycaster.setFromCamera(mousenormalized_snapped, uid_camera);

                        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
                        if (intersects.length > 0) {
                            const intersected_obj = find_intersection_object(intersects);
                            const previous_id = obj.id;
                            if (intersected_obj) {
                                const obj = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);
                                if (obj && obj.id == previous_id)
                                    show_crosshair(obj.id as Face, intersected_obj);
                            }
                        }
                    } else {
                        show_crosshair(obj.id as Face, intersected_obj);
                    }
                    hide_edge_midpoint(false, false);
                } else if (obj && obj.type == 'edge') {
                    uid_halo.show_axis();
                    show_edge_midpoint(obj.id as Edge);
                    hide_crosshair();
                } else if (obj && obj.type == 'vertex') {
                    uid_halo.show_axis();
                    hide_edge_midpoint(false, false);
                    hide_crosshair();
                }
            }
        } else {
            hide_edge_midpoint(false, false);
            hide_crosshair();
        }
    }

    if (!shift_pressed()) {
        hide_crosshair();
    }
}

export function handle_mousemove_while_drawing_edge_on_face_event(){
    // Store the raycasting direction
    addEdge_draw_raycasting_direction.copy(uid_raycaster.ray.direction);

    if (shift_was_pressed_during_adding_edge) {
        set_linear_draw_direction();

        if (uid_linear_draw_direction) {
            const adjusted_ray_origin = project_point_on_line(uid_raycaster.ray.origin, uid_camera_click_origin, uid_linear_draw_direction);
            uid_raycaster.set(adjusted_ray_origin, addEdge_draw_raycasting_direction);
        }
    }

    shoot_ray_from_raycaster_to_objects_to_trace_line(
        uid_raycaster,
        uid_raynode,
        addEdge_draw_intersected_faces,
        addEdge_draw_intersected_edges,
        addEdge_draw_intersected_vertices,
        addEdge_draw_raycasting_origins,
    );
}

export function shoot_ray_from_raycaster_to_objects_to_trace_line(raycaster: THREE.Raycaster, raynode: THREE.Object3D, intersected_faces: any[], intersected_edges: any[], intersected_vertices: any[], raycasting_origins: any[]) {
    const intersects = raycaster_intersect_objects(raycaster, raynode.children);
    if (intersects.length > 0) {
        let intersected_objects = [];
        // If carving we examine all intersections, otherwise just the first hit.
        if (App.state.carving)
            intersected_objects = find_all_intersection_objects_relevant_to_face(intersects, null);
        else {
            const intersected_obj = find_intersection_object(intersects);
            intersected_objects = [];
            if (intersected_obj)
                intersected_objects.push(intersected_obj);
        }

        let intersected_actual_object = false;
        intersected_objects.forEach(function (o) {
            const obj = uid_object3dIDToUid_object.getItem(o.object.id);
            let intersected_obj_id: 0 | Face | Edge | Vertex = 0;
            let intersected_obj_type = '';
            if (obj) {
                intersected_actual_object = true;
                intersected_obj_id = uid_object3dIDToUid_object.getItem(o.object.id)!.id;
                intersected_obj_type = uid_object3dIDToUid_object.getItem(o.object.id)!.type;
                if (intersected_obj_type == 'face') {
                    add_small_ball(o.point.x, o.point.y, o.point.z, 128, 128, 128);
                    if (intersected_faces.indexOf(intersected_obj_id) == -1) {
                        intersected_faces.push(intersected_obj_id);
                    }
                } else if (intersected_obj_type == 'edge') {
                    if (intersected_edges.indexOf(intersected_obj_id) == -1) {
                        intersected_edges.push(intersected_obj_id);
                    }
                } else if (intersected_obj_type == 'vertex') {
                    if (intersected_vertices.indexOf(intersected_obj_id) == -1) {
                        intersected_vertices.push(intersected_obj_id);
                    }
                }
            }
        });
        if (intersected_actual_object) {
            // This was a good ray, add it to the list.
            const current_raycaster_origin = new THREE.Vector3();
            current_raycaster_origin.copy(raycaster.ray.origin);
            raycasting_origins.push(current_raycaster_origin);
        }
    }
}

export function handle_mousemove_while_hovering_with_button_pressed_event() {
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    if (intersects.length > 0) {
        const intersected_obj = find_intersection_object(intersects);
        if (!intersected_obj)
            return;

        // Check if the user is in the process of setting a symmetry or mirror plane. If so, don't select objects.
        if (App.state.setting_symmetry_plane || App.state.setting_mirror_plane || App.state.selecting_tangent_perpendicular_plane) {
            if (
                intersected_obj.object.name == 'xy_axes_plane'
                || intersected_obj.object.name == 'yz_axes_plane'
                || intersected_obj.object.name == 'xz_axes_plane'
            ) {
                // Do nothing
                return;
            }
        }
    }

    if (
        !App.state.adding_vertex
        && !uid_state.adding_edge_endpoints_status
        && !App.state.offset_face_selecting_vertex
    ) {

        // Also try out the box selection
        if (uid_selection_box) {
            uid_selection_box.update(null, pointer_normalised);
            uid_selection_box.select_objects_in_box();
        }
    }
}

export function handle_mousemove_while_clickdragging_vertex(mousenormalized: THREE.Vector2, elevator_drag: boolean) {
    if (App.state.grid_visible && snap_to_grid && viewing_down_orthogonal_plane) {
        const normalized_on_grid = get_grid_point(mousenormalized, uid_grid_origin, uid_grid_normal, uid_grid_min, uid_grid_max, uid_grid_step);
        uil_drag_vertex(uid_drag_object.id, normalized_on_grid, uid_raycaster.ray.direction);
        return 1;
    } else {
        let o = new THREE.Vector3();
        const n = new THREE.Vector3();

        if (uid_state.in_first_drag_step) {
            // Before starting the drag see if we can attach a working plane
            attempt_to_attach_working_plane_to_face_for_dragging('vertex', uid_drag_object.id);
        }

        if (elevator_drag && (drag_start_movement_direction != 'free')) {
            // If this is the first shift step, then mark in which direction we're going, x, y, or z.
            // Carefull to distinguish between the actual ray origin and the vertex position.
            if (drag_start_movement_direction == 'none')
                drag_start_movement_direction = 'halo';

            // Here we perform a completely different kind of drag that I call 'elevator drag'. Basically we measure how far up or down (in 2D screen mousecoordinates)
            // the mouse has moved from the location of the drag start, and we interpret this as a drag a long the uid_halo_direction axis. We have to construct this
            // mechanism from scratch, and calculate an origin o and normal n for the drag ray.

            // Determine how far up or down the mouse has moved
            let halo_difference = 0;
            if (uid_halo) {
                halo_difference = calculate_halo_difference(mousenormalized, uid_halo.normal, uid_halo_initial_horizontal_2D_position, uid_halo_initial_vertical_2D_position);
            }

            // Project raycaster point onto halo axis
            const v1 = new THREE.Vector3();
            v1.copy(uid_raycaster.ray.direction);
            v1.cross(uid_halo.normal);
            v1.cross(uid_raycaster.ray.direction);
            o = plane_ray_intersect(uid_raycaster.ray.origin, v1, uid_halo.center, uid_halo.normal);

            /* Old way based on halo movement
            // Move along the axis by this distance to find our drag origin o.
            o.copy(uid_halo.normal);
            o.multiplyScalar(halo_difference);
            o.add(uid_halo.center);
            */

            // Find a normal to this axis, any will do really. We cross a random vector with ui_halo_direction to get a normal to the two.
            n.x = 0.316227766;
            n.y = 0.948683298;
            n.z = 0.572665981;
            n.cross(uid_halo.normal).normalize();

            // Also show the halo axis pointing towards the drag direction
            uid_halo.extend_axis(halo_difference > 0);
        } else if (working_plane_set) {
            // We'll be moving the vertex within a plane parallel to the working plane that passes through the vertex initial position.
            const b1 = plane_ray_intersect(uid_drag_object.object3d.position, working_plane_normal, uid_raycaster.ray.origin, uid_raycaster.ray.direction, 1);
            o.copy(b1);
            n.copy(working_plane_normal);
            hide_halo();
        } else {
            o.copy(uid_raycaster.ray.origin);
            n.copy(uid_raycaster.ray.direction);
            hide_halo();
        }

        uil_drag_vertex(uid_drag_object.id, o, n);

        if (snapping_is_selected) {
            const all_vertices_to_drag = [uid_drag_object.id];
            if (uid_additional_drag_vertices.length > 0)
                all_vertices_to_drag.push(...uid_additional_drag_vertices);

            identify_snapping_targets('dragging_vertices', all_vertices_to_drag, true, false);
        }

        return 1;
    }
}

function handle_press_down_for_panning() {
    uid_state.middle_mouse_button_is_pressed = true;
    App.state.panning = true;
    hide_halo();
}

function handle_press_down_for_rotating() {
    uid_state.right_mouse_button_is_pressed = true;
    App.state.rotating = true;
    // Also identify the camera target point
    uid_state.camera_target_point = find_camera_target_point_for_rotation(uid_camera);
    show_rotation_center(uid_state.camera_target_point.x, uid_state.camera_target_point.y, uid_state.camera_target_point.z, false);
    hide_halo();
}

function handle_press_down_for_box_selecting() {
    uid_selection_box = new SelectionBox();
    uid_selection_box.update(pointer_normalised, null);
}

export function handle_press_down_on_intersected_element(intersected_obj: THREE.Intersection, is_primary_button: boolean, alternate: boolean) {
    // Select first intersected object
    const o3d = uid_raynode.getObjectById(intersected_obj.object.id);

    if (!(o3d instanceof THREE.Mesh || o3d instanceof THREE.Line)) return;

    // Check whether we're handling a manipulator
    if (uid_raynode == uid_manip_raynode) {
        uid_snap_delta.set(0, 0, 0);
        uid_state.manipulator_object_was_clicked = true;
        uid_state.draggable_object_was_clicked = false;

        if (o3d.geometry.name == 'rigid_move_manip_cube') {
            uid_selected_manipulator = uid_rigid_move_manipulator!.cube;
            uid_selected_manipulator_type = 'rigid_move_manip_cube';
        } else if (o3d.geometry.name.includes('rigid_move_manip_ringx')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.ringx;
            uid_selected_manipulator_type = 'rigid_move_manip_ringx';
        } else if (o3d.geometry.name.includes('rigid_move_manip_ringy')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.ringy;
            uid_selected_manipulator_type = 'rigid_move_manip_ringy';
        } else if (o3d.geometry.name.includes('rigid_move_manip_ringz')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.ringz;
            uid_selected_manipulator_type = 'rigid_move_manip_ringz';
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectorx_tip')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisx_tip;
            uid_selected_manipulator_type = 'rigid_move_manip_vectorx_tip';
            //uid_start_scale_objects();
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectorx_rod')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisx_rod;
            uid_selected_manipulator_type = 'rigid_move_manip_vectorx_rod';
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectory_tip')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisy_tip;
            uid_selected_manipulator_type = 'rigid_move_manip_vectory_tip';
            //uid_start_scale_objects();
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectory_rod')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisy_rod;
            uid_selected_manipulator_type = 'rigid_move_manip_vectory_rod';
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectorz_tip')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisz_tip;
            uid_selected_manipulator_type = 'rigid_move_manip_vectorz_tip';
            //uid_start_scale_objects();
        } else if (o3d.geometry.name.includes('rigid_move_manip_vectorz_rod')) {
            uid_selected_manipulator = uid_rigid_move_manipulator!.axisz_rod;
            uid_selected_manipulator_type = 'rigid_move_manip_vectorz_rod';
        } else if (o3d.geometry.name == 'rigid_move_manip_planexy') {
            uid_selected_manipulator = uid_rigid_move_manipulator!.planexy;
            uid_selected_manipulator_type = 'rigid_move_manip_planexy';
        } else if (o3d.geometry.name == 'rigid_move_manip_planeyz') {
            uid_selected_manipulator = uid_rigid_move_manipulator!.planeyz;
            uid_selected_manipulator_type = 'rigid_move_manip_planeyz';
        } else if (o3d.geometry.name == 'rigid_move_manip_planexz') {
            uid_selected_manipulator = uid_rigid_move_manipulator!.planexz;
            uid_selected_manipulator_type = 'rigid_move_manip_planexz';
        } else if (o3d.geometry.name == 'rigid_move_manip_fence_tube') {
            uid_selected_manipulator = uid_rigid_move_manipulator!.fence_tube;
            uid_selected_manipulator_type = 'rigid_move_manip_fence_tube';
        } else if (o3d.geometry.name == 'edge_tangent_manip_vector_tip_1') {
            uid_selected_manipulator = uid_edge_tangent_1st_manipulator.axis_tip;
            uid_selected_manipulator_type = 'edge_tangent_manip_vector_tip_1';
        } else if (o3d.geometry.name == 'edge_tangent_manip_vector_tip_2') {
            uid_selected_manipulator = uid_edge_tangent_2nd_manipulator.axis_tip;
            uid_selected_manipulator_type = 'edge_tangent_manip_vector_tip_2';
        } else {
            uid_selected_manipulator = null;
            uid_selected_manipulator_type = '';
            uid_state.manipulator_object_was_clicked = false;
            uid_state.draggable_object_was_clicked = false;
            // The object clicked upon is not a typical ui object.
            return;
        }
        // Initialize values for the current and last origin/direction of the raycasting, to be used
        uid_manip_current_raycaster_origin.copy(uid_raycaster.ray.origin);
        uid_manip_current_raycaster_direction.copy(uid_raycaster.ray.direction);
        uid_manip_previous_raycaster_origin.copy(uid_raycaster.ray.origin);
        uid_manip_previous_raycaster_direction.copy(uid_raycaster.ray.direction);

        // Set initial raycaster hit ray
        uid_rigid_move_manipulator!.set_initial_hit_ray();
        uid_state.in_first_drag_step = true;
    } else {
        const uido = uid_object3dIDToUid_object.getItem(o3d.id);

        if (!uido) {
            // The object clicked upon is not a typical ui object.

            if (clipping_on.value) {
                // We are clipping, so the user may have clicked on a clipping plane
                if (
                    o3d.name == 'xymax_clipping_plane'
                    || o3d.name == 'xymin_clipping_plane'
                    || o3d.name == 'yzmax_clipping_plane'
                    || o3d.name == 'yzmin_clipping_plane'
                    || o3d.name == 'xzmax_clipping_plane'
                    || o3d.name == 'xzmin_clipping_plane'
                )
                    uid_state.clipping_plane_selected = o3d.name;
            } else {
                // Allow box selecting to happen
                handle_press_down_for_box_selecting();
            }
            return;
        }

        if (uido.type == 'vertex') {
            //App.state.debug_text = 'V';
            // Only drag if the LMB was clicked, we don't want to drag objects with the RMB.
            // Also not while drawing edge on face
            if (
                is_primary_button
                && !App.state.drawing_edge
                && !App.state.carving
            ) {
                uid_state.manipulator_object_was_clicked = false;
                uid_state.draggable_object_was_clicked = true;
                uid_drag_object = uido;
                uid_state.in_first_drag_step = true;

                // Record starting normalized coordinates of drag ray.
                drag_start_ray_origin.copy(uid_raycaster.ray.origin);

                // Set drag type
                if (alternate && !App.state.grid_visible) {
                    drag_start_movement_direction = 'none';
                    uid_halo_initial_horizontal_2D_position = pointer_normalised.x;
                    uid_halo_initial_vertical_2D_position = pointer_normalised.y;
                } else
                    drag_start_movement_direction = 'free';

                // Also store additional drag vertices XXX
                uid_additional_drag_vertices = [];
                //uid_additional_drag_vertex_origin_offsets = [];
                if (uid_selected_vertices.length > 0) {
                    const drag_vertex_coords = uid_drag_object.object3d.position;
                    uid_selected_vertices.values().forEach(function (v){
                        if (v.id != uid_drag_object.id) {
                            uid_additional_drag_vertices.push(v.id);
                            //let coords = new THREE.Vector3;
                            //coords.copy(v.object3d.position);
                            //coords.sub(drag_vertex_coords);
                            //uid_additional_drag_vertex_origin_offsets.push(coords);
                        }
                    });
                }
            }
        } else if (uido.type == 'edge') {
            //App.state.debug_text = 'E';
            // Only drag if the LMB was clicked, we don't want to drag objects with the RMB.
            // Also not while drawing edge on face
            if (
                is_primary_button
                && !App.state.drawing_edge
                && !App.state.carving
            ) {
                uid_state.manipulator_object_was_clicked = false;
                uid_state.draggable_object_was_clicked = true;
                uid_drag_object = uido;
                uid_state.in_first_drag_step = true;

                // Record starting normalized coordinates of drag ray.
                drag_start_ray_origin.copy(uid_raycaster.ray.origin);
                uid_dragging_raycaster_previous_origin.copy(drag_start_ray_origin);

                // Set drag type
                if (alternate && !App.state.grid_visible) {
                    drag_start_movement_direction = 'none';
                    uid_halo_initial_horizontal_2D_position = pointer_normalised.x;
                    uid_halo_initial_vertical_2D_position = pointer_normalised.y;
                } else
                    drag_start_movement_direction = 'free';
            }
        } else if (uido.type == 'face' /*&& shift_pressed()*/) {
            //App.state.debug_text = 'F';
            // Only drag if the LMB was clicked WITH shift, we don't want to drag objects with the RMB.
            // Also not while drawing edge on face
            if (
                is_primary_button
                && !App.state.drawing_edge
                && !App.state.carving
            ) {
                uid_state.manipulator_object_was_clicked = false;
                uid_state.draggable_object_was_clicked = true;
                uid_drag_object = uido;
                uid_state.in_first_drag_step = true;

                // Record starting normalized coordinates of drag ray.
                drag_start_ray_origin.copy(uid_raycaster.ray.origin);
                drag_start_hit_point.copy(intersected_obj.point);
                uid_dragging_raycaster_previous_origin.copy(drag_start_ray_origin);

                // Set drag type
                drag_start_movement_direction = 'none';
                uid_halo_initial_horizontal_2D_position = pointer_normalised.x;
                uid_halo_initial_vertical_2D_position = pointer_normalised.y;
            }
        } else {
            uid_state.manipulator_object_was_clicked = false;
            uid_state.draggable_object_was_clicked = false;
        }
    }
}

export function handle_mouseup_drag_end_events(e: MouseEvent) {
    //console_log('handle_mouseup_drag_end_events');
    suppress_animate();
    // Here we handle the end of click-drag events.

    if (uid_raynode == uid_objects_raynode) {
        if (App.state.dragging_type == 'vertex') {
            uil_end_drag_vertex(uid_drag_object.id);
            App.state.dragging_type = 'none';
            if (uid_drag_object)
                uid_unhighlight_object(uid_drag_object.object3d);
            // Snap if necessary
            const id_of_drag_object = uid_drag_object.id;
            uid_drag_object = null;
            if (snapping_is_selected)
                identify_snapping_targets('dragging_vertices', [id_of_drag_object], false, true);
        } else if (App.state.dragging_type == 'vertex_on_edge') {
            // uil_end_drag_vertex_on_edge(uid_drag_object.id, uid_drag_edge_for_vertex);
            // App.state.dragging_type = 'none';
            // uid_drag_object = null;
            // uid_drag_edge_for_vertex = null;
        } else if (App.state.dragging_type == 'edge') {
            uil_end_drag_edge(uid_drag_object.id);
            App.state.dragging_type = 'none';
            if (uid_drag_object)
                uid_unhighlight_object(uid_drag_object.object3d);
            uid_drag_object = null;
        } else {
            // Was not dragging, do nothing.
            //console_log('!!!!Bad end of drag!');
        }
        pointer_normalised_prev.x = 0;
        pointer_normalised_prev.y = 0;
    } else if (uid_raynode == uid_manip_raynode) {
        // Snap if necessary
        if (snapping_is_selected)
            identify_snapping_targets('rigid_move', uid_rigid_move_manipulator!, false, true);

        uil_end_drag_manipulator();

        // Reset manipulator colors
        uid_rigid_move_manipulator!.reset_colors();
        App.state.dragging_type = 'none';
    }

    uid_unhighlight_all_objects();
    unsuppress_animate_if_ready();
}

export function handle_mouseup_click_events(modifier: boolean) {
    // Here we handle real click events, i.e. not click-drag events.

    // If floating toolbar is visible, simply close it
    if (showing_floating_toolbar.value) {
        hide_floating_toolbar();
        return;
    }

    if (App.state.snapping_to_object) {
        // A special handling is needed here. We have already set the raynode to point to the scene so that anything
        // could be clicked, reference or regular objects, and we're waiting for the user to click on something that
        // we'll be snapping to, either edge or vertex (or possibly face).
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
        if (intersects.length > 0) {
            const intersected_obj = find_intersection_phi_or_reference_object(intersects);
            if (intersected_obj)
                finish_snap_to_object(intersected_obj);
        }
        return;
    }

    if (uid_raynode == uid_sketch_plane_raynode) {
        // Find normalized mouse coordinates and intersect scene from camera
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
        // Selecting or deselecting sketch
        // select_or_deselect_intersected_sketch(find_intersected_sketch(intersects));
    } else if (uid_raynode == uid_objects_raynode)   {
        // User clicked on regular object
        if (App.state.offset_face_dragging) {
            // User was dragging offset face and clicked somewhere (on object or in air).
            // We complete the offset.
            interrupt_offset_face();
            return;
        }

        // Find normalized mouse coordinates and intersect scene from camera
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

        if (intersects.length > 0) {
            if (App.state.adding_vertex)
                // User is in the process of adding a vertex or an edge
                handle_mouseup_click_adding_vertex_events(modifier);
            else if (App.state.offset_face_selecting_vertex)
                // User is in the process of offsetting a face
                handle_mouseup_click_offsetting_face_events();
            else
                // User is in regular operation
                handle_mouseup_click_on_object_regular_events(intersects, modifier);
        } else {
            // A right-click in the air only brings up the floating menu.
            handle_mouseup_click_in_the_air_events();
        }
        // Reset variables that should only be set for click-drag:
        uid_drag_object = null;
        uid_in_dragging_mode = false;
        uid_state.in_first_drag_step = false;
    } else if (uid_raynode == uid_ref_partstudio_faces_raynode)
        // User clicked on reference vertex
        handle_mouseup_click_reference_partstudio_face_events();
    else if (uid_raynode == uid_ref_stl_faces_raynode)
        // User clicked on reference vertex
        handle_mouseup_click_reference_stl_face_events();
    else if (uid_raynode == uid_reference_partstudio_raynode)
        // User clicked on reference vertex
        handle_mouseup_click_reference_partstudio_events();
    else if (App.state.rigid_move_manipulator)
        // User clicked while rigid move manipulator is shown
        handle_mouseup_click_rigid_move_manipulator_events();
    else if (uid_state.edge_tangent_manipulators_are_shown)
        // User clicked while rotate edge tangent manipulator is shown
        handle_mouseup_click_rotate_edge_tangent_manipulator_events(); ///XXX EDGETANGENT
}

export function handle_mouseup_click_adding_vertex_events(modifier: boolean) {
    // User clicked while adding vertex on edge
    // Find normalized mouse coordinates and intersect scene from camera
    const uido = find_currently_pointed_object();
    if (uido && uido.type == 'edge') {
        if (modifier) {
            add_vertex_on_one_or_more_selected_edges_with_shift_pressed();
            hide_edge_midpoint(true, true);
            unsuppress_animate_if_ready();
        } else if (App.state.grid_visible && snap_to_grid) {
            // Add vertex at closest grid intersection
            const normalized_on_grid = get_grid_point(
                pointer_normalised,
                uid_grid_origin,
                uid_grid_normal,
                uid_grid_min,
                uid_grid_max,
                uid_grid_step
            );
            uid_deselect_all_objects();
            const new_vertex_id = uil_add_new_vertex(uido.id as Edge, normalized_on_grid, uid_raycaster.ray.direction);
        } else {
            uid_deselect_all_objects();
            const new_vertex_id = uil_add_new_vertex(uido.id as Edge, uid_raycaster.ray.origin, uid_raycaster.ray.direction);
        }

        stop_adding_vertex();
    }
}

export function handle_mouseup_click_offsetting_face_events() {
    // User clicked while offsetting face, either while selecting the vertex or while clicking on target location.
    // Find normalized mouse coordinates and intersect scene from camera
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    const intersected_obj = find_intersection_object(intersects);

    if (!intersected_obj)
        return;

    if (App.state.offset_face_selecting_vertex) {
        const o3d = uid_raynode.getObjectById(intersected_obj.object.id);
        if (!o3d) return;
        const uido = uid_object3dIDToUid_object.getItem(o3d.id);
        if (uido!.type == 'vertex') {
            // Collect all vertices of the face
            offset_face_vertices = vertices_of_face(offset_face_object.id);
            // XXX Check that vertex is indeed on face contour.
            if (uil_is_vertex_of_face(uido!.id as Vertex, offset_face_object.id)) {
                App.state.offset_face_selecting_vertex = false;
                offset_face_vertex = uido as Uid_vertex;
                uido!.color_r = color_face_working_r;
                uido!.color_g = color_face_working_g;
                uido!.color_b = color_face_working_b;
                set_default_vertex_color(uido!.id as Vertex);

                // Integrate next step
                offset_face_object.color_r = offset_face_color_r;
                offset_face_object.color_g = offset_face_color_g;
                offset_face_object.color_b = offset_face_color_b;
                offset_face_vertex.color_r = color_r_vertex;
                offset_face_vertex.color_g = color_g_vertex;
                offset_face_vertex.color_b = color_b_vertex;
                set_default_face_color(offset_face_object.id);
                set_default_vertex_color(offset_face_vertex.id);
                animate();

                // Start offset dragging
                uid_start_offset_face(offset_face_object.id, offset_face_vertex.id, intersected_obj.point);
                animate();
            }
        }
    }
}

export function handle_mouseup_click_in_the_air_events() {
    // This was a click in the air. Deselect all objects.
    if (!mouse_near_edges(pointer_normalised))
        uid_deselect_all_objects();
}


export function handle_mouseup_click_on_object_regular_events(intersects: THREE.Intersection[], modifier: boolean) {
    // Handle case where user was drawing edge on face
    if (App.state.drawing_edge || App.state.carving) {
        // User was drawing edge on face
        stop_drawing_edge_on_face();
        return;
    }

    // Find intersection object
    const intersected_obj = find_intersection_object(intersects, true);

    if (!intersected_obj)
        return;

    // Select first intersected object
    const uido = intersection_to_uid_object(intersected_obj);
    if (!uido) {
        // The object clicked upon is not a typical ui object.

        // Handle clicking of plane to rotate viewpoint
        if (
            intersected_obj.object.name == 'xy_axes_plane'
            && !App.state.setting_symmetry_plane
            && !App.state.setting_mirror_plane
            && !App.state.selecting_tangent_perpendicular_plane
            && !(
                App.state.projecting_edges
                && global_edge_projection_data.projection_type == 'plane'
            )
        ) {
            switch_view('z', must_view_down_negative_direction('z'));
        } else if (intersected_obj.object.name == 'yz_axes_plane' &&
            !App.state.setting_symmetry_plane && !App.state.setting_mirror_plane &&
            !App.state.selecting_tangent_perpendicular_plane &&
            !(App.state.projecting_edges && global_edge_projection_data.projection_type == 'plane')) {
            switch_view('x', must_view_down_negative_direction('x'));
        } else if (
            intersected_obj.object.name == 'xz_axes_plane'
            && !App.state.setting_symmetry_plane
            && !App.state.setting_mirror_plane
            && !App.state.selecting_tangent_perpendicular_plane
            && !(
                App.state.projecting_edges
                && global_edge_projection_data.projection_type == 'plane'
            )
        ) {
            switch_view('y', must_view_down_negative_direction('y'));
        } else if (App.state.setting_symmetry_plane) {
            // Handle symmetry and mirror planes
            if (
                intersected_obj.object.name == 'xy_axes_plane'
                || intersected_obj.object.name == 'yz_axes_plane'
                || intersected_obj.object.name == 'xz_axes_plane'
            ) {
                // We have selected one of the orthogonal axis planes as a symmetry plane
                symmetry_plane_base.x = 0;
                symmetry_plane_base.y = 0;
                symmetry_plane_base.z = 0;
                symmetry_plane_normal.x = 0;
                symmetry_plane_normal.y = 0;
                symmetry_plane_normal.z = 0;
                if (intersected_obj.object.name == 'xy_axes_plane')
                    symmetry_plane_normal.z = 1;
                else if (intersected_obj.object.name == 'yz_axes_plane')
                    symmetry_plane_normal.x = 1;
                else
                    symmetry_plane_normal.y = 1;

                App.state.symmetry_plane_selected = true;
                complete_setting_symmetry_plane();
            } else {
                // Do nothing
            }
        } else if (App.state.setting_mirror_plane) {
            if (
                intersected_obj.object.name == 'xy_axes_plane'
                || intersected_obj.object.name == 'yz_axes_plane'
                || intersected_obj.object.name == 'xz_axes_plane'
            ) {
                // We have selected one of the orthogonal axis planes as a mirror plane
                mirror_plane_base.x = 0;
                mirror_plane_base.y = 0;
                mirror_plane_base.z = 0;
                mirror_plane_normal.x = 0;
                mirror_plane_normal.y = 0;
                mirror_plane_normal.z = 0;
                if (intersected_obj.object.name == 'xy_axes_plane')
                    mirror_plane_normal.z = 1;
                else if (intersected_obj.object.name == 'yz_axes_plane')
                    mirror_plane_normal.x = 1;
                else
                    mirror_plane_normal.y = 1;

                App.state.mirror_plane_selected = true;
                complete_setting_mirror_plane();
            } else {
                // Do nothing
            }
        } else if (App.state.selecting_tangent_perpendicular_plane) {
            if (
                intersected_obj.object.name == 'xy_axes_plane'
                || intersected_obj.object.name == 'yz_axes_plane'
                || intersected_obj.object.name == 'xz_axes_plane'
            ) {
                // We have selected one of the orthogonal axis planes as a mirror plane
                edge_perpendicular_plane_base.x = 0;
                edge_perpendicular_plane_base.y = 0;
                edge_perpendicular_plane_base.z = 0;
                edge_perpendicular_plane_normal.x = 0;
                edge_perpendicular_plane_normal.y = 0;
                edge_perpendicular_plane_normal.z = 0;
                if (intersected_obj.object.name == 'xy_axes_plane')
                    edge_perpendicular_plane_normal.z = 1;
                else if (intersected_obj.object.name == 'yz_axes_plane')
                    edge_perpendicular_plane_normal.x = 1;
                else
                    edge_perpendicular_plane_normal.y = 1;
                edge_perpendicular_plane_selected = true;
                complete_making_edges_perpendicular_to_plane();
            } else if (intersected_obj.object.name == 'mirror_plane') {
                edge_perpendicular_plane_base.copy(mirror_plane_base);
                edge_perpendicular_plane_normal.copy(mirror_plane_normal);
                edge_perpendicular_plane_selected = true;
                complete_making_edges_perpendicular_to_plane();
            } else if (intersected_obj.object.name == 'symmetry_plane') {
                edge_perpendicular_plane_base.copy(symmetry_plane_base);
                edge_perpendicular_plane_normal.copy(symmetry_plane_normal);
                edge_perpendicular_plane_selected = true;
                complete_making_edges_perpendicular_to_plane();
            } else {
                // Do nothing
            }
        } else if (App.state.projecting_edges && global_edge_projection_data.projection_type == 'plane') {
            // Handle selecting plane for projecting edges
            if (
                intersected_obj.object.name == 'xy_axes_plane'
                || intersected_obj.object.name == 'yz_axes_plane'
                || intersected_obj.object.name == 'xz_axes_plane'
            ) {
                if (intersected_obj.object.name == 'xy_axes_plane')
                    global_edge_projection_data.set_direction(new THREE.Vector3(0, 0, 1));
                else if (intersected_obj.object.name == 'yz_axes_plane')
                    global_edge_projection_data.set_direction(new THREE.Vector3(1, 0, 0));
                else
                    global_edge_projection_data.set_direction(new THREE.Vector3(0, 1, 0));

                uid_unhighlight_object(uid_highlighted_object_o3d);
                return;
            }
        } else if (intersected_obj.object.name == 'mirror_plane') {
            // Rotate view perpendicular to this plane
            switch_mirror_plane_perpendicular_view();
        } else if (intersected_obj.object.name == 'symmetry_plane') {
            // Rotate view perpendicular to this plane
            switch_symmetry_plane_perpendicular_view();
        }

        return;
    }

    if ((modifier || App.state.editing_reference_sketches) && uido.type == 'face') {
        // We'll select or deselect all faces in the body
        const body_id = uil_get_body_of_face(uido.id as Face);
        const this_face_is_selected = uid_selected_faces.hasItem(uido.id as Face);
        let all_faces_are_selected = this_face_is_selected;
        if (all_faces_are_selected) {
            uid_faces.keys().forEach(function (fid){
                if (uil_is_object_visible(fid) && uil_get_body_of_face(fid) == body_id) {
                    if (!uid_selected_faces.hasItem(fid))
                        all_faces_are_selected = false;
                }
            });
        }

        let all_objects_are_selected = all_faces_are_selected;
        if (all_objects_are_selected) {
            uid_edges.keys().forEach(function (eid) {
                if (uil_is_object_visible(eid) && uil_get_body_of_edge(eid) == body_id) {
                    if (!uid_selected_edges.hasItem(eid))
                        all_objects_are_selected = false;
                }
            });
        }

        if (all_objects_are_selected) {
            uid_vertices.keys().forEach(function (vid) {
                if (uil_is_object_visible(vid) && uil_get_body_of_vertex(vid) == body_id) {
                    if (!uid_selected_vertices.hasItem(vid))
                        all_objects_are_selected = false;
                }
            });
        }

        let must_select_all_objects = false;
        let must_deselect_all_objects = false;
        if (!all_faces_are_selected)
            must_select_all_objects = true;
        if (all_objects_are_selected)
            must_deselect_all_objects = true;

        suppress_animate();

        uid_faces.keys().forEach(function (fid){
            if (uil_is_object_visible(fid) && uil_get_body_of_face(fid) == body_id && singled_out_faces_to_hide.indexOf(fid) == -1) {
                if (must_select_all_objects)
                    uid_select_object(fid);
                else
                    uid_deselect_object(fid);
            }
        });

        uid_edges.keys().forEach(function (eid){
            if (uil_is_object_visible(eid) && uil_get_body_of_edge(eid) == body_id && singled_out_edges_to_hide.indexOf(eid) == -1) {
                if (must_select_all_objects)
                    uid_select_object(eid);
                else if (must_deselect_all_objects)
                    uid_deselect_object(eid);
            }
        });

        uid_vertices.keys().forEach(function (vid){
            if (uil_is_object_visible(vid) && uil_get_body_of_vertex(vid) == body_id && singled_out_vertices_to_hide.indexOf(vid) == -1) {
                if (must_select_all_objects)
                    uid_select_object(vid);
                else if (must_deselect_all_objects)
                    uid_deselect_object(vid);
            }
        });

        unsuppress_animate_if_ready();
        request_setting_button_states();
        animate();
    } else if (
        !modifier
        && uido.type == 'face'
        && (
            App.state.setting_mirror_plane
            || App.state.setting_symmetry_plane
            || App.state.selecting_tangent_perpendicular_plane
        )
    ) {
        const vs = vertices_of_face(uido.id as Face);
        const i1 = 0;
        const i2 = Math.floor(vs.length/3.0);
        const i3 = Math.floor(vs.length*2.0/3.0);
        const v1 = uid_vertices.getItem(vs[i1]!);
        const v2 = uid_vertices.getItem(vs[i2]!);
        const v3 = uid_vertices.getItem(vs[i3]!);

        if (App.state.setting_mirror_plane) {
            mirror_plane_base.copy(v1!.object3d!.position);
            mirror_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
            App.state.mirror_plane_selected = true;
            complete_setting_mirror_plane();
        } else if (App.state.setting_symmetry_plane) {
            symmetry_plane_base.copy(v1!.object3d!.position);
            symmetry_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
            App.state.symmetry_plane_selected = true;
            complete_setting_symmetry_plane();
        } else if (App.state.selecting_tangent_perpendicular_plane) {
            edge_perpendicular_plane_base.copy(v1!.object3d!.position);
            edge_perpendicular_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
            edge_perpendicular_plane_selected = true;
            complete_making_edges_perpendicular_to_plane();
        }
        animate();
        return;
    } else if (modifier && uido.type == 'vertex') {
        // We'll display an interface for entering vertex coordinates
        display_read_coords_modal(uido.id as Vertex);
    } else {
        // If it's a right-click we always select the object (i.e. don't deselect). This is because the tooltips menu will be brought up
        // and we will want to select some action on the selected objects.
        // Otherwise we toggle-select it
        if (modifier && uido.type == 'edge')
            // If it's an edge and shift is pressed, we'll try to select an entire chain of edges
            select_edge_chain(uido.id as Edge, !object_is_selected(uido.id), true);
        else {
            // Mark the click location
            if (uido.type == 'edge')
                mark_click_location_on_edge(intersected_obj.point);
            uid_toggle_select_object(uido.id, true);
        }
    }

    const coords = from_3d_coordinates_to_2d_screen_coordinates(uido.object3d.position);
    //console_log('Coords:', coords);

    // Check whether we are setting a symmetry or mirror plane and 3 vertices are selected (and nothing more)
    if (
        App.state.setting_symmetry_plane
        && uid_selected_vertices.length == 3
        && uid_selected_faces.length == 0
        && uid_selected_edges.length == 0
    ) {
        const v1 = uid_selected_vertices.getItemAtIndex(0);
        const v2 = uid_selected_vertices.getItemAtIndex(1);
        const v3 = uid_selected_vertices.getItemAtIndex(2);
        symmetry_plane_base.copy(v1!.object3d!.position);
        symmetry_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
        App.state.symmetry_plane_selected = true;
        complete_setting_symmetry_plane();
        animate();
    }

    if (
        App.state.setting_mirror_plane
        && uid_selected_vertices.length == 3
        && uid_selected_faces.length == 0
        && uid_selected_edges.length == 0
    ) {
        const v1 = uid_selected_vertices.getItemAtIndex(0);
        const v2 = uid_selected_vertices.getItemAtIndex(1);
        const v3 = uid_selected_vertices.getItemAtIndex(2);
        mirror_plane_base.copy(v1!.object3d!.position);
        mirror_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
        App.state.mirror_plane_selected = true;
        complete_setting_mirror_plane();
        animate();
    }

    if (App.state.selecting_tangent_perpendicular_plane && uid_selected_vertices.length == 3) {
        const v1 = uid_selected_vertices.getItemAtIndex(0);
        const v2 = uid_selected_vertices.getItemAtIndex(1);
        const v3 = uid_selected_vertices.getItemAtIndex(2);
        edge_perpendicular_plane_base.copy(v1!.object3d!.position);
        edge_perpendicular_plane_normal = triangle_normal(v1!.object3d!.position, v2!.object3d!.position, v3!.object3d!.position);
        edge_perpendicular_plane_selected = true;
        complete_making_edges_perpendicular_to_plane();
        animate();
    }

    if (
        !modifier
        && uido.type == 'edge'
        && uil_is_edge_linear(uido.id as Edge)
        && App.state.projecting_edges
        && global_edge_projection_data.projection_type == 'edge'
    ) {
        // We're selecting an edge to use to project edges along its normal.
        const vs = uil_get_vertices_of_edge(uido.id as Edge);
        const dir = uil_get_edge_tangent_at_vertex(uido.id as Edge, vs[0]).normalize();
        global_edge_projection_data.set_direction(dir);
    }
}

export function handle_mouseup_click_reference_partstudio_events() {
    // Find normalized mouse coordinates and intersect scene from camera to see what object we are
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    if (intersects.length == 0)
        return;

    const intersected_obj = find_intersection_object(intersects);
    if (!intersected_obj)
        return;

    const uido = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);

    if (!uido) return;

    if (uid_reference_vertices.getItem(uido.id))
        handle_mouseup_click_reference_partstudio_vertex_events();

    if (uid_reference_edges.getItem(uido.id))
        handle_mouseup_click_reference_partstudio_edge_events();

    if (uid_reference_faces.getItem(uido.id))
        handle_mouseup_click_reference_partstudio_face_events();
}

function handle_mouseup_click_reference_partstudio_vertex_events() {
    // User clicked on a partstudio reference vertex
    // Find normalized mouse coordinates and intersect scene from camera
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
}

function handle_mouseup_click_reference_partstudio_edge_events() {
    // User clicked on a partstudio reference edge
    // Find normalized mouse coordinates and intersect scene from camera
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
}

function handle_mouseup_click_reference_partstudio_face_events() {
    // User clicked on a reference face
    if (App.state.editing_reference_stl)
        return;

    // Find normalized mouse coordinates and intersect scene from camera
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    if (App.state.converting_reference_to_phi) {
        if (intersects.length > 0) {
            const intersected_obj = find_intersection_object(intersects);
            if (intersected_obj) {
                uid_toggle_select_reference_face(intersected_obj.object, false);
            }
        }
    }

    if (App.state.editing_reference_partstudio) {
        if (intersects.length > 0) {
            const intersected_obj = find_intersection_object(intersects);
            if (intersected_obj) {
                uid_toggle_select_reference_face(intersected_obj.object, true);
            }
        }
    }
}

function handle_mouseup_click_reference_stl_face_events() {
    // User clicked on a reference face

    if (App.state.editing_reference_partstudio)
        return;
    if (App.state.converting_reference_to_phi)
        return;

    // Find normalized mouse coordinates and intersect scene from camera
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    if (App.state.editing_reference_stl) {
        if (intersects.length > 0) {
            const intersected_obj = find_intersection_object(intersects);
            if (intersected_obj) {
                uid_toggle_select_reference_face(intersected_obj.object, true);
            }
        }
    }
}

export function handle_mouseup_click_rigid_move_manipulator_events() {
    let keep_manipulator_alive = false;

    // First check whether this was a click on a plane to rotate viewpoint
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    // Here we need to do an additional check to take care of the following: Since we are in the uid_manip_raynode, the raycasting will
    // not intersect any objects, just the rigid move manipulator elements. Therefore if we have a coordinate plane in front of an object,
    // and the user clicks on the coordinate plane, then the find_interesction_object will return that a coordinate plane was hit, and we will
    // rotate the viewpoint. Instead we don't want this to happen. So we will do a raycasting using the objects_raynode to see if we are indeed only
    // hitting coordinate planes or if we are hitting objects behind the coordinate plane, and use this information to avoid rotating the viewpoint.

    const objects_raynode_intersects = raycaster_intersect_objects(uid_raycaster, uid_objects_raynode.children);
    let no_actual_object_was_hit = false;
    if (objects_raynode_intersects.length == 0) {
        no_actual_object_was_hit = true;
    }

    if (intersects.length > 0) {
        const intersected_obj = find_intersection_object(intersects);
        if (!intersected_obj)
            return;

        if (intersected_obj.object.name == 'xy_axes_plane' && no_actual_object_was_hit) {
            switch_view('z', false);
            return;
        } else if (intersected_obj.object.name == 'yz_axes_plane' && no_actual_object_was_hit) {
            switch_view('x', false);
            return;
        } else if (intersected_obj.object.name == 'xz_axes_plane' && no_actual_object_was_hit) {
            switch_view('y', false);
            return;
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && [
                'rigid_move_manip_vectorx_tip',
                'rigid_move_manip_vectory_tip',
                'rigid_move_manip_vectorz_tip',
                'rigid_move_manip_vectorx_tipTrans',
                'rigid_move_manip_vectory_tipTrans',
                'rigid_move_manip_vectorz_tipTrans',
            ].includes(intersected_obj.object.geometry.name)
        ) {
            keep_manipulator_alive = true;
            if (intersected_obj.object.geometry.name.includes('rigid_move_manip_vectorx_tip'))
                display_scale_modal('x');
            else if (intersected_obj.object.geometry.name.includes('rigid_move_manip_vectory_tip'))
                display_scale_modal('y');
            else if (intersected_obj.object.geometry.name.includes('rigid_move_manip_vectorz_tip'))
                display_scale_modal('z');
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_ringx')
        ) {
            keep_manipulator_alive = true;
            display_read_value_modal('ringx');
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_ringy')
        ) {
            keep_manipulator_alive = true;
            display_read_value_modal('ringy');
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_ringz')
        ) {
            keep_manipulator_alive = true;
            display_read_value_modal('ringz');
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_vectorx_rod')
        ) {
            keep_manipulator_alive = true;
            // Set value which may be the amount that we popped out, if this is right after a pop.
            display_read_value_modal('rodx', get_display_unit_from_unitless_value(pop_amount_value));
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_vectory_rod')
        ) {
            keep_manipulator_alive = true;
            display_read_value_modal('rody');
        } else if (
            'geometry' in intersected_obj.object
            && intersected_obj.object.geometry instanceof THREE.Geometry
            && intersected_obj.object.geometry.name.includes('rigid_move_manip_vectorz_rod')
        ) {
            keep_manipulator_alive = true;
            display_read_value_modal('rodz');
        } else {
            hide_modal('readValueModal');
        }
    } else {
        hide_modal('readValueModal');
    }

    if (!mouse_near_edges(pointer_normalised) && !keep_manipulator_alive) {
        if (
            uid_raynode == uid_manip_raynode
            && uid_state.rigid_move_manipulator_shown_after_copy_body
        )
            display_message_to_user(LOG.WARN, 'The duplicate created still coincides with the original! To remove the manipulator anyway, click on the Move icon.', false);
        else {
            uid_state.rigid_move_manipulator_shown_after_copy_body = false;
            uid_hide_rigid_move_manipulator(false, 0, true);
        }
    }
}

export function handle_mouseup_click_rotate_edge_tangent_manipulator_events() {
    if (!mouse_near_edges(pointer_normalised)) {
        uid_deselect_all_objects();
        uid_toggle_edge_tangent_manipulators();
    }
}

export function handle_mouseup_nonclick_nondrag_events(finished_rotation: boolean) {
    // Occurs when user was moving mouse with button pressed,
    // and then releases mouse, and we are NOT handling a manipulator

    if (App.state.drawing_edge || App.state.carving) {
        // User has finished drawing edge on face
        stop_drawing_edge_on_face();
    } else if (App.state.editing_edge_tangent_mode && uid_raynode == uid_manip_raynode) {
        // Reset manipulator ring colors if we're using it to rotate end tangent.
        uid_rigid_move_manipulator!.reset_colors();
    } else {
        // XXX For when we're halo-dragging a face
        if (uid_raynode != uid_manip_raynode)
            unsuppress_undo_tracking_if_ready(false);
    }

    // Finish box selection
    if (uid_selection_box)
        uid_selection_box.remove();

    if (finished_rotation)
        adjust_camera_position_along_axis_and_depth(uid_camera);

    if (App.state.rigid_move_manipulator && uid_rigid_move_manipulator)
        // We may be shift-dragging the manipulator, need to reset its colors
        uid_rigid_move_manipulator!.reset_colors();

    animate();
}

export function isLeftClick(e: MouseEvent) {
    if ('button' in e)  // IE, Opera
        return (e.button == 0);
    else if ('which' in e)  // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
        // @ts-ignore
        return (e.which == 1);
    else {
        console_log_xml('Unable to determine if it\'s a left mouse click' + e);
        return false;
    }
}

export function isMiddleClick(e: MouseEvent) {
    if ('button' in e)  // IE, Opera
        return (e.button == 1);
    else if ('which' in e)  // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
        // @ts-ignore
        return (e.which == 2);
    else {
        console_log_xml('Unable to determine if it\'s a middle mouse click' + e);
        return false;
    }
}

export function isRightClick(e: MouseEvent) {
    if ('button' in e)  // IE, Opera
        return (e.button == 2);
    else if ('which' in e)  // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
        // @ts-ignore
        return (e.which == 3);
    else {
        console_log_xml('Unable to determine if it\'s a right mouse click' + e);
        return false;
    }
}

export function isRightMove(e: MouseEvent) {
    if (e.which && e.which == 3)
        return true;
    return false;
}

export function mousewheel_handler(event: WheelEvent) {
    if (ctrl_pressed())
        return true;

    // get wheel direction
    const d = typeof event.deltaY != 'undefined'
        ? -event.deltaY
        : event.detail;

    if (!App.state.zoom_blocked && d !== 0)
        zoom(sign(d) as 1 | -1, event, 1.0);
    else
        console_log_xml('Trying to zoom while zoom_blocked is true');
    return event.preventDefault();
}

export function get_normalized_mouse_coordinates(e: MouseEvent | PointerEvent) {
    //console_log("pageX:", pagex, "pageY:", pagey, "xOffset:", xOffset, "yOffset:", yOffset);
    const mousex = e.pageX - xOffset;
    const mousey = e.pageY - yOffset;
    return new THREE.Vector2(
        (mousex / App.state.canvas.width) * 2 - 1,
        -((mousey / App.state.canvas.height) * 2 - 1),
    );
}

export const should_run_consistency_checks = () =>
    !App.state.symmetry_plane_selected
    && (
        App.state.always_run_consistency_checks_on_vertices
        || App.state.always_run_consistency_checks_on_rest
    ) && !App.state.drawing_edge
    && !App.state.carving
    && !App.state.offset_face_dragging
    && !App.state.offset_face_selecting_vertex
    && !App.state.rotating;
